import * as React from 'react';
import MuiTextField, { TextFieldProps as MuiTextFieldProps } from '@mui/material/TextField';
import uniqueId from 'lodash-es/uniqueId';
import { css, styled } from '../../styles';
import { getHelperText, HelperTextProps } from './HelperText';
import { BpDesignSystem } from '../../types';
import { Typography } from '../Typography/Typography';
import { FormHelperTextProps } from '@mui/material/FormHelperText';
import { InputLabelProps } from '@mui/material/InputLabel';
import { InputProps as MuiInputProps } from '@mui/material/Input';

export type InputProps = BpDesignSystem.Props<
  Omit<MuiTextFieldProps, 'color' | 'variant' | 'select' | 'inputProps'> & {
    label?: React.ReactNode;
    subLabel?: string;
    hasChips?: boolean;
    autoHeight?: boolean;
    helperText?: React.ReactNode;
    errorText?: React.ReactNode;
    helperTextProps?: HelperTextProps;
    endAdornment?: React.ReactNode;
    startAdornment?: React.ReactNode;
    variant?: 'standard' | 'filled';
    readOnly?: boolean;
    min?: number | string;
    max?: number | string;
    step?: number;
    minLength?: number;
    maxLength?: number;
    /**
     * To support TimePicker
     */
    InputProps?: {
      endAdornment?: React.ReactNode;
      startAdornment?: React.ReactNode;
    };
  }
>;

export const Input = React.forwardRef(
  (
    {
      autoHeight,
      label,
      subLabel,
      id,
      hasChips,
      helperText,
      errorText,
      dataTest,
      helperTextProps,
      className,
      endAdornment,
      startAdornment,
      slotProps,
      variant = 'standard',
      InputProps,
      readOnly,
      min,
      max,
      step,
      minLength,
      maxLength,
      ...rest
    },
    ref
  ) => {
    const newId = id ?? uniqueId('bp-design-system-input') + Date.now();

    const targetLabel = (
      <>
        {!!label && <span>{label}</span>}
        {!!subLabel && (
          <Typography className="sub-label" variant="body" color="colorOnSurfaceVariant">
            {subLabel}
          </Typography>
        )}
      </>
    );

    return (
      <StyledTextField
        className={className}
        required={rest.required}
        error={rest.error}
        fullWidth={rest.fullWidth}
        label={!!label || !!subLabel ? targetLabel : undefined}
        data-component-role="input"
        data-test={dataTest}
        $hasChips={hasChips}
        $autoHeight={autoHeight || hasChips}
        id={newId}
        slotProps={{
          ...slotProps,
          formHelperText: { ...(slotProps?.formHelperText as FormHelperTextProps), ...helperTextProps },
          inputLabel: {
            shrink: true,
            ...slotProps?.inputLabel
          } as InputLabelProps,
          input: {
            readOnly: readOnly,
            startAdornment,
            endAdornment,
            disableUnderline: true,
            ...slotProps?.input,
            ...InputProps
          } as MuiInputProps,
          htmlInput: {
            min,
            max,
            step,
            minLength,
            maxLength,
            ...slotProps?.htmlInput,
            // To support @mui DatePicker/DateTimePicker
            ...(rest as any).inputProps
          }
        }}
        variant={variant}
        helperText={!!helperText || !!errorText ? getHelperText(rest.error, helperText, errorText) : undefined}
        {...rest}
        ref={ref}
      />
    );
  }
) as BpDesignSystem.ForwardedFC<InputProps, HTMLDivElement>;

const StyledTextField = styled(MuiTextField)<{ $hasChips?: boolean; $autoHeight?: boolean }>`
  && {
    height: ${({ $autoHeight }) => ($autoHeight ? 'auto' : undefined)};
      
      .MuiInputLabel-root {
        display: grid;
        grid-template-columns: ${({ required }) => (required ? 'min-content min-content' : 'min-content')};
        grid-template-rows: auto auto;
        .sub-label {
            order: 3;
        }
      }
      
      
    ${({ $hasChips }) => ($hasChips ? hasChipsCss : undefined)}
      
  }
`;

const hasChipsCss = css`
  && {
    display: inline-flex;
    padding: 4px 56px 2px 8px; // now: should be: 34px
    position: relative;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;

    input {
      width: 0;
      display: flex;
      flex-grow: 1;
      flex-wrap: wrap;
      align-items: center;
      padding-left: 0;
      border-radius: 2px;
      text-overflow: ellipsis;
      justify-content: flex-start;
    }

    .MuiInputAdornment-positionStart {
      max-height: 150px;
      overflow: hidden auto;
      padding-left: 0;
    }

    .MuiInputAdornment-positionEnd {
      top: calc(50% - 14px);
      right: 0;
      position: absolute;
      height: auto;
    }
  }
`;
